import { useFormik } from 'formik';
import useRedditAdvertiserMutator, {
	MutatingAdvertiser,
} from 'wb-reddit/app/routes/advertisers/hooks/useRedditAdvertiserMutator';
import {
	alephToast,
	Button,
	ButtonColor,
	CheckIcon,
	getObjectsDiff,
	Input,
	LoaderBars,
	NotificationToast,
	NotificationType,
	useDebouncedMemo,
	useOpen,
	isReddit,
	isSpotify,
	getNewAvatar,
	getRandomMainColor,
	handleErrorNotification,
} from '@aleph/shared-components';
import React, { useEffect, useMemo, useRef } from 'react';
import ProfileImageUploader from 'wb-reddit/app/common/components/profileImageUploader';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
	ActivateAgencyAccountDocument,
	AdvertiserOwnerOptionsAutocompleteDocument,
	AgencyAccessOptionsQueryItems,
	AgencyAccessOptionsQueryUsersUsers,
	InviteUserToAgencyDocument,
	LinkGroupToAgencyDocument,
	RedditAdvertiserListDocument,
	TransferAdvertiserOwnershipDocument,
	UpdateRedditAdvertiserDocument,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import debounce from 'debounce-promise';
import { canUserEditAgency } from 'wb-reddit/app/routes/advertisers/utils';
import AgencyAccessListItem from 'wb-reddit/app/routes/advertisers/mutator/accessListItem';
import { Placeholder } from 'rsuite';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { object, string } from 'yup';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { useNavigate } from 'react-router-dom';
import { userHasOneOfRoles } from 'wb-reddit/app/routes/utils';
import OwnerChangeDialog from 'wb-reddit/app/routes/advertisers/components/ownerChangeDialog';
import EntityEditInputSuffix from 'wb-reddit/app/common/components/entityEditInputSuffix';
import { MutatorProps } from 'wb-reddit/app/routes/advertisers/mutator/container';
import AdvertisterOwnerAutocomplete from 'wb-reddit/app/routes/advertisers/mutator/advertisterOwnerAutocomplete';
import AdvertiserAccessAutocomplete from 'wb-reddit/app/routes/advertisers/mutator/advertiserAccessAutocomplete';
import { Trans, useTranslation } from 'react-i18next';
import {
	AuthGraphGrantAdvertiserAccountAccessDocument,
	AuthGraphSearchOwnerDocument,
	AuthGraphUpdateAdvertiserAccountDocument,
	AuthGraphSearchOwnerQuery,
	AdvertiserAccountAccess,
	UserAccountLevel as AuthGraphUserAccountLevel,
} from 'wb-spotify/graphql/authgraph/generated';
import { SPOTIFY_ADMIN_ROLES } from 'wb-spotify/app/common/utils';
import { captureException } from '@sentry/react';

const RedditAdvertiserEditMutator = ({ updateRow, setShouldShowSaveBeforeExit }: MutatorProps) => {
	const { t } = useTranslation();

	const navigate = useNavigate();
	const { mutatingAdvertiser, setMutatingAdvertiser } = useRedditAdvertiserMutator();
	const isUpdate = !!mutatingAdvertiser?.agencyAccountId;
	const [confirmOwnerChangeOpen, { onOpen: openOwnerChange, onClose: closeOwnerChange }] = useOpen(false);
	const oldOwner = useMemo(() => mutatingAdvertiser?.agencyAccountOwner, []);

	const [searchOwners] = useLazyQuery(
		isReddit()
			? AdvertiserOwnerOptionsAutocompleteDocument
			: AuthGraphSearchOwnerDocument,
	);

	const [inviteUser, { loading: invitingUser }] = useMutation(InviteUserToAgencyDocument, {
		errorPolicy: 'all',
	});

	const [linkGroup, { loading: linkingGroup }] = useMutation(LinkGroupToAgencyDocument, {
		errorPolicy: 'all',
	});

	const [grantAdvertiserAccountAccess, { loading: grantingAccess }] = useMutation(AuthGraphGrantAdvertiserAccountAccessDocument, {
		errorPolicy: 'all',
	});

	const [updateAdvertiser, { loading: updating }] = useMutation(isReddit()
		? UpdateRedditAdvertiserDocument
		: AuthGraphUpdateAdvertiserAccountDocument, {
		errorPolicy: 'all',
	});

	const [activateAdvertiser, { loading: activating }] = useMutation(ActivateAgencyAccountDocument, {
		errorPolicy: 'all',
	});

	const debouncedSearchOwners = useMemo(() => debounce(searchOwners, 300), []);

	const platformUser = useUserMetaData();
	const shouldSeeActivate = !mutatingAdvertiser?.agencyAccountActivatedAt
		&& mutatingAdvertiser?.agencyAccountId
		&& platformUser?.userAccountLevel === UserAccountLevel.Admin;

	const loadOwnerOptions = async (search: string) => {
		if (!search) return [];

		if (isReddit() && oldOwner?.userEmail && oldOwner?.userEmail?.split('@')?.[1] !== search?.split('@')?.[1]) {
			return [];
		}

		const { data, error } = await debouncedSearchOwners({
			variables: {
				search,
			},
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: something went wrong while searching for users',
					'Something went wrong while searching for users',
				),
			);

			return [];
		}

		if (isSpotify()) {
			return (data as unknown as AuthGraphSearchOwnerQuery)?.users?.items
				?.map(item => ({ ...item, label: `${item?.userName} ${item?.userSurname || ''}` })) ?? [];
		}

		return data?.users ?? [];
	};

	const initialValues = useRef({
		advertiserAccountAccess: [] as unknown as AdvertiserAccountAccess[],
		...mutatingAdvertiser as MutatingAdvertiser,
		salesforceId:            mutatingAdvertiser?.salesforceId ?? '',
	});

	const {
		values,
		submitForm,
		setFieldValue,
		handleBlur,
		handleChange,
		errors,
		touched,
		resetForm,
		isSubmitting,
		setFieldTouched,
	} = useFormik({
		initialValues:    initialValues.current,
		validationSchema: object().shape({
			agencyAccountName:  string().required(t('re: advertiser.validation.name', 'Advertiser name is required.')),
			agencyAccountOwner: object().required(t('re: advertiser.validation.owner', 'Advertiser owner is required.')),
		}),
		initialTouched: {
			agencyAccountProfilePicture: !!mutatingAdvertiser?.agencyAccountProfilePicture,
		},
		onSubmit: async (submitValue) => {
			const variables = isReddit() ? {
				agencyAccountId:   mutatingAdvertiser?.agencyAccountId ?? '',
				agencyAccountName: submitValue.agencyAccountName,
				salesforceId:      submitValue.salesforceId,
				// @ts-expect-error:
				// Initial value is a string, after user uploads image it becomes a ProfileImage object
				profilePicture:    submitValue.agencyAccountProfilePicture?.file ?? undefined,
			} : {
				advertiserAccountId:      mutatingAdvertiser?.agencyAccountId ?? '',
				advertiserAccountName:    submitValue.agencyAccountName,
				// @ts-expect-error:
				advertiserAccountOwnerId: submitValue.agencyAccountOwner.id,
				appId:                    import.meta.env.VITE_APP_ID,
			};

			const { errors: updateErrors, data } = await updateAdvertiser({
				// @ts-ignore
				variables,
			});

			if (updateErrors || !data) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={
							t(
								'sc: action err',
								{
									action:       'updating the advertiser',
									defaultValue: 'Something went wrong while updating the advertiser',
								},
							)
						}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<Trans
							i18nKey="re: advertiser update success notification"
							defaults="Advertiser <strong>{{agencyAccountName}}</strong> successfully {{action}}."
							values={{
								agencyAccountName: submitValue.agencyAccountName,
								action:            shouldSeeActivate
									? t('re: advertiser.status.approved', 'approved').toLocaleLowerCase()
									: t('re: advertiser.status.updated', 'updated').toLowerCase(),
							}}
						>
							Advertiser <strong>{submitValue.agencyAccountName}</strong> successfully
							{shouldSeeActivate
								? t('re: advertiser.status.approved', 'approved').toLocaleLowerCase()
								: t('re: advertiser.status.updated', 'updated').toLowerCase()
							}.
						</Trans>
					}
				/>,
			);

			if (isSpotify()) {
				updateRow?.({
					// @ts-ignore
					row: data.updateAdvertiserAccount,
				});
			} else {
				updateRow?.({
					// @ts-ignore
					row: data.updateAgencyAccount,
				});
			}

			// User also changed owner
			if (
				(submitValue?.agencyAccountOwner?.id && initialValues.current.id)
				&& (submitValue.agencyAccountOwner.id !== initialValues.current.agencyAccountOwner?.id)
			) {
				openOwnerChange();
			} else {
				navigate('/advertisers');
				setMutatingAdvertiser(undefined);
			}
		},
	});

	const dirtyFields = useDebouncedMemo(
		() => {
			/**
			 * Exclude the user fields as they are changed inline, without submitting the form
			 */
			const { userGroups, users, ...restOfValues } = values;
			const {
				userGroups: initialUserGroups,
				users: initialUsers,
				...initialRestOfValues
			} = initialValues.current;

			const fields = getObjectsDiff(restOfValues, initialRestOfValues, true);

			// TODO remove when profile picture would be ready on BE
			if (isSpotify()) {
				delete fields.agencyAccountProfilePicture;
			}

			setShouldShowSaveBeforeExit(!!Object.keys(fields).length);

			return fields;
		},
		[values],
		150,
	);

	const [transferOwnership, { loading: transferingOwnership }] = useMutation(TransferAdvertiserOwnershipDocument, {
		refetchQueries: [RedditAdvertiserListDocument],
	});

	const userGroups = useMemo(() => {
		if (isReddit()) return values?.userGroups;

		return values?.advertiserAccountAccess?.map(item => item?.userGroup);
	}, [values]);

	const userCanEdit = canUserEditAgency(values, platformUser)
		|| (!isUpdate && userHasOneOfRoles(platformUser, [UserAccountLevel.Agency, UserAccountLevel.Advertiser]));

	const agencyOwner = values?.agencyAccountOwner;

	// TODO: Refactor function params types -> https://alephrnd.atlassian.net/browse/WBNR-1450
	const onAccessEntitySelected = async (option: AgencyAccessOptionsQueryItems | AgencyAccessOptionsQueryUsersUsers) => {
		if (isSpotify()) {
			const agencyOption = option as AgencyAccessOptionsQueryItems;

			if (isUpdate) {
				const { errors: grantErrors } = await grantAdvertiserAccountAccess({
					variables: {
						userGroupId:         agencyOption.userGroupId,
						advertiserAccountId: mutatingAdvertiser?.id ?? '',
						appId:               import.meta.env.VITE_APP_ID,
					},
				});

				if (grantErrors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={
								t(
									're: something went wrong while granting access to group',
									'Something went wrong while granting access to group',
								)
							}
						/>,
					);

					return;
				}

				alephToast(
					<NotificationToast
						type={NotificationType.Success}
						title={
							<Trans
								i18nKey="re: team granted access to advertiser success notification"
								values={{ userGroupName: agencyOption.userGroupName, agencyAccountName: values.agencyAccountName }}
								defaults="Team <strong>{{userGroupName}}</strong> has been granted access to <strong>{{agencyAccountName}}</strong>."
							>
								Team <strong>{agencyOption.userGroupName}</strong> has been granted access to <strong>{values.agencyAccountName}</strong>.
							</Trans>
						}
					/>,
				);
			}

			setFieldValue('advertiserAccountAccess', [...values.advertiserAccountAccess ?? [], { userGroup: agencyOption }]);

			updateRow?.({
				row: {
					...values,
					// @ts-ignore
					advertiserAccountAccess: [...values.advertiserAccountAccess ?? [], { userGroup: agencyOption }],
				},
			});

			return;
		}

		if (option?.__typename === 'SearchUser') {
			if (isUpdate) {
				const { errors: inviteErrors, data } = await inviteUser({
					variables: {
						agencyAccountId: mutatingAdvertiser?.agencyAccountId ?? '',
						userIds:         [option.userId],
					},
				});

				if (inviteErrors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={
								t(
									're: something went wrong while inviting the user',
									'Something went wrong while inviting the user',
								)
							}
						/>,
					);

					return;
				}

				alephToast(
					<NotificationToast
						type={NotificationType.Success}
						title={
							<Trans
								i18nKey="re: user invited to advertiser success notification"
								defaults="User <strong>{{userFullName}}</strong> has been invited to <strong>{{agencyAccountName}}</strong>."
								values={{
									userFullName:      option.userFullName,
									agencyAccountName: values.agencyAccountName,
								}}
							>
								User <strong>{option.userFullName}</strong> has been invited to <strong>{values.agencyAccountName}</strong>.
							</Trans>
						}
					/>,
				);

				if (data?.linkUsersToAgencyAccount) {
					setFieldValue('users', data?.linkUsersToAgencyAccount?.users ?? []);

					const initialAdvertiserData = initialValues.current as MutatingAdvertiser;

					updateRow?.({
						row: {
							...initialAdvertiserData,
							users: data?.linkUsersToAgencyAccount?.users ?? [],
						},
					});

					return;
				}
			}

			setFieldValue('users', [...values.users ?? [], {
				__typename: 'AgencyUser',
				user:       {
					...option,
					// TODO: This will be removed when backend adds username and surname to SearchUser type
					userName:    option?.userFullName?.split(' ')?.[0] ?? '',
					userSurname: option?.userFullName?.split(' ')?.[1] ?? '',
				},
			}]);
		}

		if (option?.__typename === 'UserGroup') {
			if (isUpdate) {
				const { errors: inviteErrors } = await linkGroup({
					variables: {
						agencyAccountId: mutatingAdvertiser?.agencyAccountId ?? '',
						groupIds:        [option.userGroupId],
					},
				});

				if (inviteErrors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={
								t(
									're: something went wrong while inviting the user',
									'Something went wrong while inviting the user',
								)
							}
						/>,
					);

					return;
				}

				alephToast(
					<NotificationToast
						type={NotificationType.Success}
						title={
							<Trans
								i18nKey="re: team granted access to advertiser success notification"
								values={{ userGroupName: option.userGroupName, agencyAccountName: values.agencyAccountName }}
								defaults="Team <strong>{{userGroupName}}</strong> has been granted access to <strong>{{agencyAccountName}}</strong>."
							>
								Team <strong>{option.userGroupName}</strong> has been granted access to <strong>{values.agencyAccountName}</strong>.
							</Trans>
						}
					/>,
				);
			}

			setFieldValue('userGroups', [...values.userGroups ?? [], option]);

			updateRow?.({
				row: {
					...values,
					userGroups: [...values.userGroups ?? [], option],
				},
			});
		}
	};

	useEffect(() => {
		setMutatingAdvertiser(values);
	}, [values]);

	const imageColor = useMemo(() => getRandomMainColor(), []);

	useEffect(() => {
		if (!touched.agencyAccountProfilePicture) {
			getNewAvatar({ entityType: 'Advertiser', background: imageColor })
				.then((newImage) => {
					setFieldValue('agencyAccountProfilePicture', newImage);
				});
		}
	}, [values.agencyAccountName]);

	const onTransferOwner = async () => {
		try {
			if (isReddit()) {
				await transferOwnership({
					variables: {
						agencyAccountId: mutatingAdvertiser?.agencyAccountId ?? '',
						userId:          values.agencyAccountOwner?.userId ?? '',
					},
				});
			}

			if (isSpotify()) {
				await updateAdvertiser({
					variables: {
						// @ts-ignore
						advertiserAccountId:      mutatingAdvertiser?.agencyAccountId ?? '',
						advertiserAccountOwnerId: values.agencyAccountOwner?.userId ?? '',
						// TODO https://alephrnd.atlassian.net/browse/WB-4605
						advertiserAccountName:    mutatingAdvertiser?.agencyAccountName,
						appId:                    import.meta.env.VITE_APP_ID,
					},
				});
			}

			navigate('/advertisers');
			setMutatingAdvertiser(undefined);
		} catch (e) {
			captureException(e);

			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						t(
							're: advertiser.ownership.transfer.err',
							'Something went wrong while transferring the ownership',
						)
					}
				/>,
			);
		}
	};

	async function handleActivateAdvertister() {
		if (!mutatingAdvertiser) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						t(
							're: something went wrong while activating the advertiser',
							'Something went wrong while activating the advertiser',
						)
					}
				/>,
			);

			return;
		}

		const { errors: activateErrors, data: activatedData } = await activateAdvertiser({
			variables: {
				agencyAccountId: mutatingAdvertiser?.agencyAccountId ?? '',
			},
		});

		if (activateErrors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={
						t(
							're: something went wrong while activating the advertiser',
							'Something went wrong while activating the advertiser',
						)
					}
				/>,
			);
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={
					<Trans
						i18nKey="re: advertiser activated success notification"
						defaults="Advertiser <strong>{{agencyAccountName}}</strong> successfully activated."
						values={{
							agencyAccountName: mutatingAdvertiser.agencyAccountName,
						}}
					>
						Advertiser <strong>{mutatingAdvertiser.agencyAccountName}</strong> successfully activated.
					</Trans>
				}
			/>,
		);

		if (activatedData) {
			updateRow?.({
				row: activatedData.activateAgencyAccount,
			});
		}

		navigate('/advertisers');
		setMutatingAdvertiser(undefined);
	}

	const isUpdatingAdvertiser = useMemo(
		() => isSubmitting || updating || activating || transferingOwnership,
		[isSubmitting, updating, activating, transferingOwnership],
	);

	const userCanChangeOwnership = useMemo(() => {
		if (isSpotify()) {
			if (SPOTIFY_ADMIN_ROLES.includes(platformUser?.userAccountLevel as unknown as AuthGraphUserAccountLevel)) {
				return true;
			}

			return userCanEdit && !isUpdate && platformUser?.id === oldOwner?.id;
		}

		return userHasOneOfRoles(platformUser, [UserAccountLevel.Admin]);
	}, [userCanEdit, isUpdate, platformUser, oldOwner]);

	// TODO
	const onRemove = (group: any) => {
		setFieldValue(
			'advertiserAccountAccess',
			(values.advertiserAccountAccess ?? []).filter(item => item?.userGroup?.userGroupId !== group.userGroupId),
		);

		updateRow?.({
			row: {
				...values,
				// @ts-ignore
				advertiserAccountAccess: (values.advertiserAccountAccess ?? []).filter(item => item?.userGroup?.userGroupId !== group.userGroupId),
			},
		});
	};

	return (
		<div className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]">
			{confirmOwnerChangeOpen
				&& <OwnerChangeDialog
					onClose={closeOwnerChange}
					onSubmit={onTransferOwner}
					oldOwner={oldOwner}
					newOwner={values?.agencyAccountOwner}
				/>
			}

			<div className="flex flex-1 justify-center w-[25%]">
				<ProfileImageUploader
					profileImage={values.agencyAccountProfilePicture}
					onUploadProfileImage={image => {
						setFieldValue('agencyAccountProfilePicture', image);
						submitForm();
					}}
					type='Advertiser'
					readOnly={!userCanEdit}
				/>
			</div>

			<div className="flex flex-1 flex-col gap-[24px] min-w-[50%] min-h-[100%] px-[2px]">
				<div className="font-semibold text-[14px]">
					{t('re: advertiser general information title', 'General Information')}
				</div>
				<Input
					id="agencyAccountName"
					className="w-full"
					label={t('re: advertiser name', 'Advertiser name')}
					value={values.agencyAccountName}
					onChange={handleChange}
					onBlur={handleBlur}
					disabled={!userCanEdit || isUpdatingAdvertiser}
					validationError={touched.agencyAccountName ? errors.agencyAccountName : undefined}
					suffix={
						<EntityEditInputSuffix
							loading={isSubmitting}
							onConfirm={submitForm}
							onCancel={() => resetForm({
								values: {
									...values,
									agencyAccountName: initialValues.current.agencyAccountName ?? '',
								},
							})}
							showSuffix={dirtyFields.agencyAccountName}
						/>
					}
				/>

				<div>
					<AdvertisterOwnerAutocomplete
						// @ts-ignore
						loadOptions={loadOwnerOptions}
						values={values}
						onOptionClick={(option) => setFieldValue('agencyAccountOwner', option)}
						disabled={!userCanChangeOwnership || isUpdatingAdvertiser}
						setTouched={() => setFieldTouched('agencyAccountOwner', true)}
						triggerProps={{
							suffix: <EntityEditInputSuffix
								loading={isUpdatingAdvertiser}
								onConfirm={openOwnerChange}
								onCancel={() => resetForm({
									values: {
										...values,
										// @ts-ignore
										agencyAccountOwner: isReddit()
											? initialValues.current?.agencyAccountOwner
											: initialValues.current?.owner,
									},
								})}
								showSuffix={dirtyFields.agencyAccountOwner}
							/>,
						}}
					/>

					{touched.agencyAccountOwner && errors.agencyAccountOwner
							&& <div className="text-red-500 text-[10px] px-[8px]">{errors.agencyAccountOwner}</div>
					}
				</div>

				{isReddit() && (
					<Input
						id="salesforceId"
						label={t('re: salesforce customer id', 'Salesforce customer ID')}
						value={values.salesforceId ?? ''}
						onChange={handleChange}
						disabled={!userCanEdit || isUpdatingAdvertiser}
						onBlur={handleBlur}
						validationError={touched.agencyAccountName ? errors.salesforceId
							: undefined}
						suffix={
							<EntityEditInputSuffix
								loading={isSubmitting}
								onConfirm={submitForm}
								onCancel={() => resetForm({
									values: {
										...values,
										salesforceId: initialValues.current.salesforceId ?? '',
									},
								})}
								showSuffix={dirtyFields.salesforceId}
							/>
						}
					/>
				)}

				<div className="font-semibold text-[14px] pt-[16px]">{t('re: advertiser access title', 'ACCESS')}</div>

				<AdvertiserAccessAutocomplete
					disabled={!agencyOwner || !userCanEdit || isUpdatingAdvertiser}
					onOptionClick={onAccessEntitySelected}
					selectedGroupIds={userGroups?.map(group => group.userGroupId) ?? []}
				/>

				<div>
					<div className="flex h-[32px] items-center text-gray-600 px-[8px] text-[11px] border-b
							border-b-gray-200"
					>
						<div className="flex-1">{t('re: advertiser access table contributor header', 'CONTRIBUTOR')}</div>
						<div className="px-[8px]">{t('re: advertiser access table domain header', 'DOMAIN')}</div>
						<div className="w-[50px] flex justify-center">{t('re: advertiser access table revoke header', 'REVOKE')}</div>
					</div>

					{(values.agencyAccountOwner || values.owner) && (
						<AgencyAccessListItem
							// @ts-ignore
							entity={{
								...(values.agencyAccountOwner || values.owner),
								isOwner: true,
							}}
							disabled={isSubmitting}
						/>
					)}

					{userGroups?.map(group => (
						<AgencyAccessListItem
							key={group.id}
							entity={group}
							disabled={!userCanEdit || isSubmitting}
							onRemove={() => {
								if (isSpotify()) {
									onRemove(group);

									return;
								}

								setFieldValue(
									'userGroups',
									// TODO check with authgraph how mutaion will reply
									(values.userGroups ?? []).filter(g => g.id !== group.id),
								);

								updateRow?.({
									row: {
										...values,
										userGroups: values.userGroups?.filter(g => g.id !== group.id) ?? [],
									},
								});
							}}
						/>
					))}

					{!isUpdate && platformUser
							&& <AgencyAccessListItem
								disabled={!userCanEdit || isSubmitting}
								entity={{
									user: platformUser, agencyIsOwner: false, you: true, __typename: 'AgencyUser',
								}}
							/>
					}

					{values.users?.filter(user => !user?.agencyIsOwner)?.map(user => (
						<AgencyAccessListItem
							disabled={!userCanEdit}
							key={user?.user?.id}
							entity={{
								...user,
								you: platformUser?.userId === user?.user?.userId,
							}}
							onRemove={() => {
								setFieldValue(
									'users',
									(values.users ?? []).filter(u => u.user?.id !== user?.user?.id),
								);
							}}
						/>
					))
					}

					{(invitingUser || linkingGroup || grantingAccess)
							&& <div className="flex h-[48px] gap-[16px] items-center px-[8px]">
								<Placeholder.Graph
									active
									className="!h-[32px] !w-[32px] min-w-[32px] !rounded-full"
								/>

								<Placeholder.Graph
									active
									className="!h-[16px]"
								/>
							</div>
					}
				</div>
			</div>

			<div className="flex flex-col gap-[16px] flex-1 w-[25%]">
				<div className="font-semibold text-[14px]">{t('re: advertiser sidebar title', 'WISE.BLUE')}</div>

				<div className="flex text-[12px]">
					<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
						<div>{t('re: advertiser sidebar id', 'ID')}</div>
						<div>{t('re: advertiser sidebar access', 'Access')}</div>
						<div>{t('re: advertiser sidebar salesforce', 'Salesforce')}</div>
					</div>
					<div className="text-black flex flex-col gap-[8px]">
						<div className="text-gray-800">
							{values.agencyAccountId ?? '-'}
						</div>
						<div>
							{values?.agencyAccountId ? <AccessBadge
								accessEntities={
									values?.clients?.map(client => ({
										type: 'CLIENT',
										name: client.clientName ?? client.adAccountName,
									})) ?? []
								}
							/> : '-'
							}
						</div>
						<div>
							{values.salesforceId ? <a
								href={`https://alephholding.my.salesforce.com/${values.salesforceId}`}
								target="_blank"
								className="text-blue hover:text-blue-600"
								rel="noreferrer"
							>
								{t('re: advertiser sidebar open salesforce profile', 'Open profile')}
							</a> : <span className="text-gray-800">-</span>
							}
						</div>
					</div>
				</div>
			</div>

			{shouldSeeActivate
				&& <div
					className="absolute bottom-[32px] left-[50%] -translate-x-1/2
						flex gap-[16px] p-[24px] shadow-around-sm bg-white rounded-lg"
				>
					<Button
						title={t('re: advertiser.approve_publish', 'Approve publishing')}
						leftNode={
							isSubmitting ? <LoaderBars className="animate-spin" /> : <CheckIcon />
						}
						color={ButtonColor.GREEN}
						disabled={isSubmitting}
						loading={isSubmitting}
						onClick={handleActivateAdvertister}
					/>
				</div>
			}
		</div>
	);
};

export default RedditAdvertiserEditMutator;
